import React, { useState, useCallback, useMemo } from 'react'

import { SearchOutlined, ClearOutlined, LinkOutlined, ReloadOutlined } from '@ant-design/icons'
import { Form, Button, Table, Space, Spin, Alert, Progress, Typography, Statistic, Row, Col, InputNumber } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

const { Text } = Typography

export default function SluggishProductsPage({
  role = 'admin',
  service, // New
  addJob,
  useSingleNotification,
  selectedMerchantId = null
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [queryStatus, setQueryStatus] = useState('idle') // idle, querying, processing, complete, failed

  const [progressData, setProgressData] = useState(null)
  const [jobId, setJobId] = useState(null)
  const [results, setResults] = useState([])
  const [summary, setSummary] = useState(null)
  const [error, setError] = useState(null)

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  // 監聽 Job 狀態更新（透過 WebSocket）
  useSingleNotification(jobId, {
    onCompleted: useCallback((data) => {
      console.log('[SluggishProducts] Job completed:', data)

      // WebSocket 資料結構: data.data = Worker 返回的結果
      const workerResult = data?.data

      if (workerResult?.success && Array.isArray(workerResult?.data)) {
        setQueryStatus('complete')
        setResults(workerResult.data)
        setSummary(workerResult.summary)
      } else {
        setQueryStatus('failed')
        setError(workerResult?.error || 'Query failed: Invalid data format')
      }
    }, []),

    onFailed: useCallback((data) => {
      console.log('[SluggishProducts] Job failed:', data)
      setQueryStatus('failed')
      setError(data?.error || data?.message || 'Query failed')
    }, []),

    onProgress: useCallback((data) => {
      console.log('[SluggishProducts] Job progress:', data)
      setProgressData(data?.progress)
    }, [])
  })

  const handleSubmit = useCallback(async (values) => {
    setLoading(true)
    setQueryStatus('querying')
    setError(null)
    setResults([])
    setSummary(null)
    setProgressData(null)

    try {
      const requestBody = {
        months: values.months,
        max_sales: values.max_sales,
        min_stock: values.min_stock
      }

      // Operator 角色需要傳遞 merchant_id
      if (role === 'operator') {
        if (!selectedMerchantId) {
          setError('Please select a merchant first')
          setQueryStatus('failed')
          setLoading(false)
          return
        }
        requestBody.merchant_id = selectedMerchantId
      }

      console.log('[SluggishProducts] Submit with requestBody:', requestBody)

      const result = await service.query(requestBody);

      if (result.success && result.data?.job_id) {
        const newJobId = result.data.job_id
        setJobId(newJobId)
        setQueryStatus('processing')

        // 註冊 Job 到 BackgroundJobManager
        addJob(newJobId, 'shared.sluggish_products.job_name')
      } else {
        throw new Error(result.message || 'Query failed')
      }
    } catch (err) {
      console.error('Submit error:', err)
      setError(err.message)
      setQueryStatus('failed')
    } finally {
      setLoading(false)
    }
  }, [service, addJob, t, role, selectedMerchantId])

  const handleReset = useCallback(() => {
    form.resetFields()
    setQueryStatus('idle')
    setResults([])
    setSummary(null)
    setError(null)
    setProgressData(null)
    setJobId(null)
  }, [form])

  // 從結果中提取所有品牌選項（用於篩選）
  const brandFilters = useMemo(() => {
    const brands = [...new Set(results.map(item => item.brand).filter(Boolean))]
    return brands.map(brand => ({ text: brand, value: brand }))
  }, [results])

  const columns = useMemo(() => [
    {
      title: t('shared.sluggish_products.result.columns.product_name'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/products/${record.id}`} style={{ wordBreak: 'break-word' }}>
            {name}
          </Link>
          <a
            href={`${urlPrefix}/products/${record.id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      title: t('shared.sluggish_products.result.columns.sku'),
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: t('shared.sluggish_products.result.columns.barcode'),
      dataIndex: 'barcode',
      key: 'barcode',
    },
    {
      title: t('shared.sluggish_products.result.columns.brand'),
      dataIndex: 'brand',
      key: 'brand',
      filters: brandFilters,
      onFilter: (value, record) => record.brand === value,
      filterSearch: true,
    },
    {
      title: t('shared.sluggish_products.result.columns.sales_quantity'),
      dataIndex: 'sales_quantity',
      key: 'sales_quantity',
      align: 'right',
      sorter: (a, b) => (a.sales_quantity || 0) - (b.sales_quantity || 0),
      render: (value) => <Text type="warning">{value || 0}</Text>
    },
    {
      title: t('shared.sluggish_products.result.columns.total_stock'),
      dataIndex: 'total_stock',
      key: 'total_stock',
      align: 'right',
      sorter: (a, b) => (a.total_stock || 0) - (b.total_stock || 0),
      defaultSortOrder: 'descend',
      render: (value) => <Text strong>{value || 0}</Text>
    },
    {
      title: t('shared.sluggish_products.result.columns.available_quantity'),
      dataIndex: 'available_quantity',
      key: 'available_quantity',
      align: 'right',
      sorter: (a, b) => (a.available_quantity || 0) - (b.available_quantity || 0),
      render: (value) => <Text type="success">{value || 0}</Text>
    },
    {
      title: t('common.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
    },
  ], [t, urlPrefix, brandFilters])

  return (
    <div>
      {/* 查詢表單 — inline filter bar */}
      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        initialValues={{
          months: 3,
          max_sales: 10,
          min_stock: 100
        }}
        style={{ marginBottom: 24 }}
      >
        <Form.Item
          label={t('shared.sluggish_products.query_form.months')}
          name="months"
          tooltip={t('shared.sluggish_products.query_form.months_help')}
          rules={[
            { required: true, message: t('shared.sluggish_products.query_form.validations.months_required') },
            { type: 'number', min: 1, message: t('shared.sluggish_products.query_form.validations.months_min') }
          ]}
        >
          <InputNumber
            placeholder={t('shared.sluggish_products.query_form.months_placeholder')}
            min={1}
            style={{ width: 120 }}
          />
        </Form.Item>

        <Form.Item
          label={t('shared.sluggish_products.query_form.max_sales')}
          name="max_sales"
          tooltip={t('shared.sluggish_products.query_form.max_sales_help')}
          rules={[
            { required: true, message: t('shared.sluggish_products.query_form.validations.max_sales_required') },
            { type: 'number', min: 0, message: t('shared.sluggish_products.query_form.validations.max_sales_min') }
          ]}
        >
          <InputNumber
            placeholder={t('shared.sluggish_products.query_form.max_sales_placeholder')}
            min={0}
            style={{ width: 120 }}
          />
        </Form.Item>

        <Form.Item
          label={t('shared.sluggish_products.query_form.min_stock')}
          name="min_stock"
          tooltip={t('shared.sluggish_products.query_form.min_stock_help')}
          rules={[
            { required: true, message: t('shared.sluggish_products.query_form.validations.min_stock_required') },
            { type: 'number', min: 0, message: t('shared.sluggish_products.query_form.validations.min_stock_min') }
          ]}
        >
          <InputNumber
            placeholder={t('shared.sluggish_products.query_form.min_stock_placeholder')}
            min={0}
            style={{ width: 120 }}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
              disabled={queryStatus === 'processing' || (role === 'operator' && !selectedMerchantId)}
            >
              {t('common.button.search')}
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleReset}
              disabled={queryStatus === 'processing'}
            >
              {t('shared.sluggish_products.query_form.clear')}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* 查詢狀態 */}
      {queryStatus === 'processing' && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">{t('shared.sluggish_products.status.processing')}</Text>
          </div>
          {progressData && (
            <Progress
              percent={Math.round((progressData.current / progressData.total) * 100)}
              status="active"
              format={(percent) => `${progressData.current}/${progressData.total} (${percent}%)`}
              style={{ maxWidth: 400, margin: '16px auto 0' }}
            />
          )}
        </div>
      )}

      {/* 錯誤訊息 */}
      {queryStatus === 'failed' && error && (
        <Alert
          message={t('shared.sluggish_products.status.failed')}
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" danger onClick={handleReset}>
              <ReloadOutlined /> {t('common.button.retry')}
            </Button>
          }
        />
      )}

      {/* 查詢結果 */}
      {queryStatus === 'complete' && (
        <>
          {/* 摘要統計 */}
          {summary && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title={t('shared.sluggish_products.query_form.months')}
                  value={summary.months}
                  suffix={t('common.unit.months')}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('shared.sluggish_products.query_form.max_sales')}
                  value={summary.max_sales}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('shared.sluggish_products.query_form.min_stock')}
                  value={summary.min_stock}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('shared.sluggish_products.result.summary', { count: summary.total_count })}
                  value={summary.total_count}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
            </Row>
          )}

          {/* 結果列表 */}
          {results.length === 0 ? (
            <Alert
              message={t('shared.sluggish_products.result.empty')}
              type="info"
              showIcon
            />
          ) : (
            <Table
              columns={columns}
              dataSource={results}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `${t('common.table.total')}: ${total}`
              }}
            />
          )}
        </>
      )}
    </div>
  )
}
